"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Mail,
  Mic,
  Briefcase,
  Lock,
  MessageSquare,
  Handshake,
  Clock,
  CheckCircle2,
  ChevronRight,
  Globe,
  Shield,
  Sparkles,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { ContactForm } from "@/components/ui/forms";
import { PillarGridBlock } from "@/lib/blocks/pillar-grid-block";
import {
  Stethoscope,
  Compass,
  Phone,
  Video,
  MapPin,
  BookOpen,
  Newspaper,
  Users,
  GraduationCap,
  Award,
  ShieldCheck,
} from "lucide-react";

const INQUIRY_PATHS = [
  {
    icon: Mic,
    title: "Speaking Inquiries",
    summary: "Keynote addresses, fireside chats, panel discussions, and workshop facilitation at conferences, corporate events, universities, and leadership forums.",
    href: "/contact/#speaking",
    accent: "red" as const,
    details: [
      "Event name and organizing body",
      "Date, time, and location",
      "Audience size and profile",
      "Preferred topic from six signature themes",
      "Speaking format (keynote, fireside, panel, workshop)",
      "Budget range and logistical requirements",
    ],
    response: "Acknowledged within two business days",
  },
  {
    icon: Briefcase,
    title: "Executive Advisory & Board Service",
    summary: "Deep, sustained engagements for organizations seeking operational discipline, governance expertise, and strategic clarity.",
    href: "/contact/#advisory",
    accent: "purple" as const,
    details: [
      "Organization name and sector",
      "The operational challenge or governance need",
      "Desired engagement model (retainer, project, board)",
      "Timeline and scope of mandate",
      "Current governance structure",
    ],
    response: "Reviewed within three business days",
  },
  {
    icon: Lock,
    title: "Operational Consulting",
    summary: "Discreet, high-stakes engagements at sovereign scale. By introduction only. Governed by the most stringent confidentiality protocols.",
    href: "/contact/#consulting",
    accent: "emerald" as const,
    details: [
      "The nature of the operational challenge",
      "Organizational context (sector, scale, geography)",
      "Desired outcomes",
      "Introduction source",
      "Urgency and timeline",
      "Confidentiality requirements",
    ],
    response: "Reviewed under national security level confidentiality from first contact",
  },
  {
    icon: MessageSquare,
    title: "Press & Media Inquiries",
    summary: "Expert commentary, interviews, and background briefings on operational excellence, women in leadership, governance transformation, and strategic intelligence.",
    href: "/contact/#press",
    accent: "amber-gold" as const,
    details: [
      "Publication or media outlet name",
      "The topic or story being covered",
      "Deadline and format requirements",
      "Specific questions or angles",
      "Attribution preferences (on record, background, etc.)",
    ],
    response: "Typically acknowledged within one business day",
  },
  {
    icon: Handshake,
    title: "Partnership & Collaboration",
    summary: "Strategic alliances and joint initiatives aligned with operational excellence, women's empowerment, and governance transformation.",
    href: "/contact/#partnership",
    accent: "cyan" as const,
    details: [
      "Organization name and mission",
      "The proposed collaboration",
      "Expected outcomes and timeline",
      "Alignment with core domains",
      "Resource commitment",
    ],
    response: "Reviewed within five business days",
  },
  {
    icon: Mail,
    title: "General Contact",
    summary: "All other inquiries including academic collaborations, organizational development, speaking bureau, and general correspondence.",
    href: "/contact/#general",
    accent: "denim" as const,
    details: [
      "Clear description of the inquiry",
      "Organizational context",
      "Desired outcome",
    ],
    response: "Acknowledged within two business days",
  },
];

const ACCENT: Record<typeof INQUIRY_PATHS[number]["accent"], { text: string; bg: string; border: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/20", pill: "bg-red/15 text-red" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/20", pill: "bg-purple/15 text-purple" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/20", pill: "bg-emerald/15 text-emerald" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/20", pill: "bg-amber-gold/15 text-amber-gold" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/20", pill: "bg-cyan/15 text-cyan" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/20", pill: "bg-denim/15 text-denim" },
};

const RESPONSE_STAGES = [
  { stage: "01", title: "Acknowledgment", body: "Every inquiry receives a confirmation of receipt, typically within 24 hours, indicating which engagement path has been selected and the expected timeline for a substantive response." },
  { stage: "02", title: "Diagnostic Conversation", body: "A structured conversation with the appropriate lead — via secure video or telephone — to understand the situation fully before any operational details are disclosed or proposals prepared." },
  { stage: "03", title: "Tailored Proposal", body: "A scope-specific proposal outlining the engagement model, terms, deliverables, and accountability structure. No boilerplate. Every proposal is calibrated to the engagement at hand." },
  { stage: "04", title: "Confirmation & Onboarding", body: "Engagements begin only after mutual confidence is established. Onboarding includes secure communication setup, confidentiality agreements, and the first diagnostic working session." },
];

export default function ContactPage() {
  return (
    <>
      <PageHero
        icon={Mail}
        overline="Contact & Engagements"
        overlineClassName="text-amber-gold"
        title={
          <>
            Begin an <span className="text-gradient-premium">Engagement.</span>
          </>
        }
        subtitle="Every significant engagement begins with a single conversation. The diagnostic phase — understanding the situation before prescribing the engagement — is the foundation."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Contact" }]}
        badge="By introduction · Absolute discretion"
      />

      <Section background="default" className="py-12 lg:py-16">
        <div className="text-center mb-10">
          <h2 className="text-display-md mb-3">Select the inquiry path.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each path is purpose-built to capture the right information at the right stage — accelerating the path from inquiry to impact.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {INQUIRY_PATHS.map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.button
                key={p.title}
                onClick={() => {
                  document.getElementById(p.href.split("#")[1])?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`group relative glass-card rounded-2xl p-5 text-left overflow-hidden bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-28 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative flex items-start gap-3">
                  <div className="size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0">
                    <p.icon className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-sm mb-1 ${a.text}`}>{p.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed line-clamp-3">{p.summary}</p>
                    <div className={`mt-2 inline-flex items-center gap-1 text-caption font-semibold ${a.text}`}>
                      Begin <ChevronRight className="size-3" />
                    </div>
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      </Section>

      {INQUIRY_PATHS.map((p, i) => {
        const a = ACCENT[p.accent];
        return (
          <Section
            key={p.title}
            id={p.href.split("#")[1]}
            ariaLabel={p.title}
            background={i % 2 === 0 ? "muted" : "default"}
            className="py-16 lg:py-20"
          >
            <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-12 items-start">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass mb-5">
                  <p.icon className={`size-3.5 ${a.text}`} />
                  <span className={`text-overline ${a.text}`}>Path {String(i + 1).padStart(2, "0")}</span>
                </div>
                <h2 className="text-display-md mb-4">{p.title}</h2>
                <p className="text-body-md text-muted-foreground leading-relaxed mb-6">
                  {p.summary}
                </p>
                <h3 className="text-heading-sm mb-3">What information is needed</h3>
                <ul className="space-y-2 mb-6">
                  {p.details.map((d) => (
                    <li key={d} className="flex items-start gap-2.5 text-body-sm text-muted-foreground">
                      <CheckCircle2 className={`size-4 mt-0.5 shrink-0 ${a.text}`} />
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md ${a.pill} text-caption font-semibold`}>
                  <Clock className="size-3" />
                  Response: {p.response}
                </div>
              </div>

              <div className="glass-strong rounded-2xl p-6 gradient-border sticky top-24">
                <ContactForm accent="amber-gold" />
              </div>
            </div>
          </Section>
        );
      })}

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <h2 className="text-display-md mb-3">What happens after the inquiry.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The response process is as disciplined and transparent as the engagements themselves. Every stage is defined. Every timeline is committed to.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {RESPONSE_STAGES.map((s, i) => (
            <motion.div
              key={s.stage}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="glass-card rounded-2xl p-5 relative"
            >
              <div className="text-display-sm font-bold text-amber-gold/30 leading-none mb-2">{s.stage}</div>
              <h3 className="text-heading-md mb-2">{s.title}</h3>
              <p className="text-body-sm text-muted-foreground leading-relaxed">{s.body}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.5fr] gap-8 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-tinted-cyan border-cyan/20 mb-4">
              <Shield className="size-3.5 text-cyan" />
              <span className="text-overline text-cyan">Standard of Confidentiality</span>
            </div>
            <h2 className="text-display-md mb-3">Absolute discretion.</h2>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The standard that governs every engagement. National security level confidentiality from first contact.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { icon: Lock, title: "Compartmentalized Operations", body: "Every team sees only what it needs to see. Unified command sees everything." },
              { icon: Shield, title: "National Security NDAs", body: "Binding agreements ensuring absolute secrecy among all parties. Zero exceptions." },
              { icon: Globe, title: "30+ Countries", body: "Active operations across three continents. Sovereign-grade delivery in any environment." },
              { icon: CheckCircle2, title: "Zero Security Incidents", body: "Fifteen-plus years of zero breaches. The record that permits the work to continue at sovereign scale." },
            ].map((b, i) => (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className="glass-card rounded-xl p-4 bg-gradient-to-br from-cyan/10 to-transparent border border-cyan/20"
              >
                <b.icon className="size-5 text-cyan mb-2.5" />
                <h3 className="text-heading-sm mb-1.5">{b.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{b.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 items-start">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-tinted-emerald border-emerald/20 mb-5">
              <Compass className="size-3.5 text-emerald" />
              <span className="text-overline text-emerald">The Contact Framework</span>
            </div>
            <h2 className="text-display-md mb-5">A diagnostic approach to every inquiry.</h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              Dr. Jyoti Kush accepts inquiries across five primary engagement categories: keynote speaking, executive advisory and board service, operational consulting, press and media commentary, and partnership or general inquiries. Each inquiry path is purpose-built to capture the right information at the right stage — ensuring the diagnostic conversation that follows is substantive, efficient, and grounded in context.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The response standard is professional, discreet, and timely. All inquiries are treated with the confidentiality that governs sovereign-level operations. No inquiry is too small to merit serious consideration; no engagement is too complex to be scoped with precision.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              Dr. Jyoti Kush approaches every inbound inquiry the same way a physician approaches a patient presenting with symptoms: listen first, diagnose thoroughly, prescribe only after the full picture is understood. This diagnostic method — developed over a decade of operating at sovereign scale — ensures that every engagement is precisely calibrated to the situation at hand.
            </p>
          </div>

          <div className="glass-strong rounded-2xl p-6 lg:p-8 gradient-border lg:sticky lg:top-24">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="size-4 text-red" />
              <span className="text-overline text-red">New Delhi Headquarters</span>
            </div>
            <h3 className="text-display-sm mb-3">Global Operations, Local Anchor.</h3>
            <p className="text-body-sm text-muted-foreground leading-relaxed mb-5">
              Primary operations are headquartered in New Delhi, India — the central coordination node from which planning, strategic coordination, and engagement management are directed across eighteen countries on three continents.
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3 rounded-lg bg-muted/30 border border-border/30 p-3">
                <div className="size-9 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0">
                  <MapPin className="size-4 text-white" />
                </div>
                <div>
                  <div className="text-heading-sm mb-0.5">In-Person at Client Location</div>
                  <p className="text-caption text-muted-foreground leading-relaxed">Board meetings, on-site consulting, keynote addresses, and operational briefings. Travel planned for efficiency and security.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-lg bg-muted/30 border border-border/30 p-3">
                <div className="size-9 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0">
                  <ShieldCheck className="size-4 text-white" />
                </div>
                <div>
                  <div className="text-heading-sm mb-0.5">In-Person at New Delhi HQ</div>
                  <p className="text-caption text-muted-foreground leading-relaxed">Secure meeting facilities, intelligence-grade analytical tools, and the full operational resource base.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-lg bg-muted/30 border border-border/30 p-3">
                <div className="size-9 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0">
                  <Video className="size-4 text-white" />
                </div>
                <div>
                  <div className="text-heading-sm mb-0.5">Remote via Secure Communications</div>
                  <p className="text-caption text-muted-foreground leading-relaxed">Advisory consultations, media commentary, and ongoing engagements through encrypted infrastructure.</p>
                </div>
              </div>
            </div>
            <div className="mt-5 flex items-center gap-2 text-caption text-muted-foreground">
              <Clock className="size-3.5" />
              <span>Time zone coordination managed across three continents. Geographic distance does not compromise engagement quality.</span>
            </div>
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <PillarGridBlock
          eyebrow="Standard of Confidentiality"
          title="Confidentiality Standards."
          description="Six operating standards that govern every engagement from first contact to final report. The framework that permits sovereign-scale work to continue at the level where it operates."
          columns={3}
          variant="numbered"
          pillars={[
            {
              icon: <ShieldCheck className="size-5" />,
              title: "National Security Level Protocols",
              body: "All inquiries and engagements are handled under the same confidentiality standards that govern national security operations — non-disclosure agreements, compartmentalized communication, and secure document handling that meets or exceeds government-grade standards.",
              accent: "emerald",
            },
            {
              icon: <Users className="size-5" />,
              title: "Compartmentalized Operations",
              body: "Information is shared on a strict need-to-know basis. No single individual other than Dr. Jyoti Kush herself has access to the full scope of an engagement. Even in the unlikely event of a personnel breach, no comprehensive information is exposed.",
              accent: "emerald",
            },
            {
              icon: <Lock className="size-5" />,
              title: "Secure Communications Infrastructure",
              body: "All sensitive communications are conducted through encrypted channels. Document transmission follows military-grade encryption standards. No unencrypted communication of sensitive material is permitted under any circumstances.",
              accent: "emerald",
            },
            {
              icon: <Award className="size-5" />,
              title: "Zero-Compromise Track Record",
              body: "Zero security incidents across fifteen-plus years of sovereign-level engagements. Zero breaches. Zero leaks. The record is not accidental — it is the product of systematic discipline and an uncompromising commitment to confidentiality.",
              accent: "emerald",
            },
            {
              icon: <Compass className="size-5" />,
              title: "Diagnostic-Phase Confidentiality",
              body: "The diagnostic conversation is conducted under the same confidentiality standards that govern all operational work. No information disclosed during the diagnostic is shared externally, recorded without authorization, or used beyond engagement assessment.",
              accent: "emerald",
            },
            {
              icon: <Phone className="size-5" />,
              title: "Continuous Operational Discipline",
              body: "Confidentiality is not a policy add-on. It is an operational discipline — embedded in every process, every communication, and every engagement. The standard established at first contact is maintained throughout the engagement and beyond.",
              accent: "emerald",
            },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-tinted-amber-gold border-amber-gold/20 mb-4">
            <Briefcase className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">The Full Engagement Portfolio</span>
          </div>
          <h2 className="text-display-md mb-4">Seven service categories. One operating method.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Dr. Jyoti Kush&apos;s engagement portfolio spans seven primary service categories, each grounded in the operational discipline and diagnostic method that define the work. Detailed specifications are available through the inquiry process.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { icon: Mic, title: "Keynote Speaking", body: "Six signature topics — each grounded in a decade of sovereign-scale operations. Standalone addresses, fireside chats, panels, or workshops.", accent: "red" },
            { icon: Briefcase, title: "Executive Advisory", body: "Ongoing operational counsel following the physician&apos;s method: diagnose, prescribe, monitor, adjust. Retainer, project, or embedded models.", accent: "purple" },
            { icon: Users, title: "Board Service", body: "Governance and operational oversight at the board level — operational discipline of a COO and diagnostic rigor of a physician applied to governance.", accent: "emerald" },
            { icon: Lock, title: "Operational Consulting", body: "Discreet, high-stakes operational transformation for governments and leaders. Conducted under national security level confidentiality.", accent: "amber-gold" },
            { icon: GraduationCap, title: "Executive Mentorship", body: "One-on-one leadership development for executives and emerging leaders. Assessment, diagnosis, prescribed development, and measured progress.", accent: "cyan" },
            { icon: BookOpen, title: "Corporate Workshops", body: "Team training on operational excellence frameworks, the 70% Operating Model, and the clinical method for leadership.", accent: "denim" },
            { icon: Newspaper, title: "Press & Media Commentary", body: "Expert commentary on operational excellence, women&apos;s leadership, governance transformation, and strategic intelligence.", accent: "lime" },
          ].map((s, i) => {
            const a = ACCENT[s.accent as keyof typeof ACCENT] || ACCENT.denim;
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <s.icon className={`size-6 ${a.text} mb-3`} />
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{s.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: s.body }} />
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-tinted-cyan border-cyan/20 mb-4">
              <Stethoscope className="size-3.5 text-cyan" />
              <span className="text-overline text-cyan">About the Operator</span>
            </div>
            <h2 className="text-display-md mb-4">The operator behind the engagements.</h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-4">
              Dr. Jyoti Kush is the Chief Operating Officer of CryptoMize and MaxiMize Infinium — a Digital Conglomerate operating at the intersection of AI-powered intelligence, military-grade security, and political-grade strategy across eighteen countries on three continents. A physician by training — MBBS, Gynaecology, Delhi — she has spent a decade co-building and running one of the most demanding technology organizations on earth.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-4">
              Her leadership model is distinctive: teams that are over seventy percent women, governed by the clinical method developed from medical training, and measured by outcomes that speak for themselves. She is the creator of The 70% Project — the operating case for women in leadership — and the publisher of The Operator&apos;s Notebook, a biweekly publication on operational excellence.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The engagements accepted through these channels are an extension of this operational identity: rigorous, substantive, and grounded in a decade of lived experience at the highest tier of global operations.
            </p>
          </div>

          <div className="space-y-3">
            {[
              { q: "How quickly will a response be received?", a: "Response timelines are defined by inquiry type. Press and media inquiries: acknowledgment within one business day. Speaking and general inquiries: within two business days. Advisory, consulting, and partnership inquiries: within three business days." },
              { q: "Is the inquiry confidential?", a: "All inquiries are treated with the discretion that governs sovereign-level operations from the moment of first contact. National security level confidentiality protocols — including non-disclosure agreements, compartmentalized communication, and secure document handling — govern all communications throughout the engagement lifecycle." },
              { q: "What is the engagement process?", a: "The engagement process follows six defined stages: Inquiry, Acknowledgment, Diagnostic Conversation, Prescription (Proposal), Engagement, and Feedback and Evolution. Each stage is governed by the diagnostic discipline that defines the work — transparent, structured, and designed for precision." },
              { q: "How far in advance should a speaking inquiry be submitted?", a: "Speaking inquiries should be submitted as early as possible — ideally six to nine months before the event date. This timeline allows for the diagnostic conversation, topic development, and logistical coordination. Shorter timelines may be accommodated depending on schedule availability." },
              { q: "Can Dr. Jyoti Kush customize a speaking topic?", a: "Yes. While six signature topics are available, a custom topic can be developed in consultation with event organizers during the diagnostic conversation — aligned with the event&apos;s thematic focus, audience composition, and organizational challenges." },
              { q: "Does Dr. Jyoti Kush offer virtual engagements?", a: "Yes. Advisory consultations, media commentary, corporate workshops, and select speaking engagements are available via secure, encrypted communications infrastructure. The virtual model maintains the same confidentiality and quality standards as in-person engagements." },
            ].map((f, i) => (
              <motion.div
                key={f.q}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="glass-card rounded-2xl p-5 bg-gradient-to-br from-cyan/10 to-transparent border border-cyan/20"
              >
                <h3 className="text-heading-sm mb-2 text-cyan">{f.q}</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: f.a }} />
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Speaking Topics", description: "Six signature keynotes drawn from a decade of sovereign-scale operations.", href: "/speaking/", icon: Mic, accent: "red" },
          { title: "Services & Engagements", description: "Tailored engagement modalities calibrated to the stakes.", href: "/services/", icon: Briefcase, accent: "purple" },
          { title: "The 70% Project", description: "The operating case for women in leadership. A decade of evidence.", href: "/the-70-percent-project/", icon: Sparkles, accent: "amber-gold" },
          { title: "About Dr. Jyoti Kush", description: "The physician who became an operator at sovereign scale.", href: "/about/", icon: Mail, accent: "cyan" },
        ]}
      />
    </>
  );
}
